import React, { useState } from 'react'
import { AdCreative, LeadCard } from '@marketing-milk/interfaces'
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Popover,
  TextField,
} from '@mui/material'
import { CANDIDATE_CAP } from './useLeadCardCandidates'

type Props = {
  selected: AdCreative[]
  forms: LeadCard[]
  campaignForms: LeadCard[]
  loadingForms: boolean
  truncated: boolean
  canCreate: boolean
  onMove: (form: LeadCard | null) => void
  onAISort: (ads: AdCreative[], forms: LeadCard[]) => void
  onBuild: (mode: 'copy' | 'blank') => void
  onClearSelection: () => void
  onDone: () => void
}

export const MoveToFormBar = ({
  selected,
  forms,
  campaignForms,
  loadingForms,
  truncated,
  canCreate,
  onMove,
  onAISort,
  onBuild,
  onClearSelection,
  onDone,
}: Props) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const [sorting, setSorting] = useState(false)
  const [sortForms, setSortForms] = useState<LeadCard[]>([])
  const selectedLabel = `${selected.length} ad${selected.length === 1 ? '' : 's'}`
  const alreadyAssigned = selected.filter(c => c.leadCardUsed).length
  const groupForForm = (form: LeadCard) =>
    campaignForms.some(f => f.id === form.id) ? 'Used in this campaign' : 'Other forms'
  const build = (mode: 'copy' | 'blank') => {
    setAnchor(null)
    onBuild(mode)
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-semibold text-mm-neutral-1-900">
          {selected.length ? `${selectedLabel} selected` : 'Select ads to get started'}
        </span>
        {selected.length > 0 && (
          <button
            type="button"
            onClick={onClearSelection}
            className="text-sm text-mm-primary-700 hover:underline"
          >
            Clear selection
          </button>
        )}
        <Button
          variant="contained"
          disabled={selected.length === 0}
          onClick={event => setAnchor(event.currentTarget)}
          aria-haspopup="dialog"
          aria-expanded={!!anchor}
        >
          Assign to form…
        </Button>
        {selected.length > 0 && (
          <>
            <Button
              onClick={() => {
                setSortForms(campaignForms)
                setSorting(true)
              }}
            >
              Sort with AI…
            </Button>
            {alreadyAssigned > 0 && <Button onClick={() => onMove(null)}>Remove form</Button>}
          </>
        )}
        <div className="ml-auto">
          <Button variant="outlined" onClick={onDone}>
            Done
          </Button>
        </div>
      </div>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <div role="dialog" aria-label="Assign to form" className="w-screen max-w-md space-y-4 p-4">
          <div>
            <h3 className="text-base font-semibold">Assign {selectedLabel} to a form</h3>
            <p className="mt-1 text-sm text-mm-neutral-1-600">
              Choose a form to assign it immediately.
            </p>
          </div>
          <Autocomplete
            options={forms}
            groupBy={groupForForm}
            loading={loadingForms}
            value={null}
            getOptionLabel={form => form.formName}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_, form) => {
              if (!form) return
              setAnchor(null)
              onMove(form)
            }}
            renderInput={params => (
              <TextField {...params} autoFocus label="Find an existing form" />
            )}
          />
          {truncated && (
            <p className="text-xs text-mm-neutral-1-500">
              Includes the {CANDIDATE_CAP} most recently created forms, plus forms used here.
            </p>
          )}
          <div className="border-t border-mm-neutral-1-200 pt-3">
            <h4 className="text-sm font-semibold">Create new form</h4>
            <p className="mt-1 text-sm text-mm-neutral-1-600">
              Your selected ads will use the new form when you save.
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button
                variant="outlined"
                disabled={!canCreate || forms.length === 0}
                onClick={() => build('copy')}
              >
                Copy existing form
              </Button>
              <Button disabled={!canCreate} onClick={() => build('blank')}>
                Start blank
              </Button>
            </div>
            {!canCreate && (
              <p className="mt-2 text-sm text-mm-neutral-1-600">
                Connect a Facebook Page to this business to create forms.
              </p>
            )}
          </div>
        </div>
      </Popover>
      <Dialog open={sorting} onClose={() => setSorting(false)} fullWidth maxWidth="sm">
        <DialogTitle>Choose forms for AI sorting</DialogTitle>
        <DialogContent>
          <p className="mb-4 text-sm text-mm-neutral-1-600">
            AI will match your {selectedLabel} to the forms you choose below.
            {alreadyAssigned > 0 &&
              ` This replaces existing assignments on ${alreadyAssigned} ad${
                alreadyAssigned === 1 ? '' : 's'
              }.`}
          </p>
          <Autocomplete
            multiple
            options={forms}
            groupBy={groupForForm}
            loading={loadingForms}
            value={sortForms}
            onChange={(_, next) => setSortForms(next)}
            getOptionLabel={form => form.formName}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={params => <TextField {...params} label="Forms AI can choose from" />}
          />
          {truncated && (
            <p className="mt-2 text-xs text-mm-neutral-1-500">
              Includes the {CANDIDATE_CAP} most recently created forms, plus forms used here.
            </p>
          )}
          <p className="mt-3 text-sm text-mm-neutral-1-600">
            Choose at least two forms. Ads AI cannot match will stay unchanged.
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSorting(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={sortForms.length < 2}
            onClick={() => {
              setSorting(false)
              onAISort(selected, sortForms)
            }}
          >
            Sort {selectedLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
